import {
  AlertTriangle,
  ArrowRight,
  CircleCheck,
  Clock,
  House,
  type LucideIcon,
  Wallet
} from 'lucide-react'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { useLandlordDashboard } from '@services/dashboard/queries/useLandlordDashboard'
import { useFlats } from '@services/flats/queries/useFlats'
import { useRents } from '@services/contracts/queires/useRents'
import { useProfile } from '@services/users/queries/useProfile'
import { FlatList, PlaceOutput, RentModel } from 'types/api'

import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'

type RentRow = RentModel & { flat?: string | null; flat_title?: string | null }

const MONEY_TONE = {
  amber: 'bg-peach/25 text-warning',
  green: 'bg-success-soft text-success',
  teal: 'bg-primary/10 text-primary'
} as const

const FLAT_STATUS_TONE: Record<string, string> = {
  AVAILABLE: 'bg-success-soft text-success',
  RENTED: 'bg-info-soft text-info',
  DRAFT: 'bg-peach/25 text-warning',
  NOT_AVAILABLE: 'bg-ink/[0.07] text-ink/60'
}

const eur = (n?: number | string | null) => `${Number(n ?? 0).toFixed(2)} €`
const formatDate = (v?: string | null) =>
  v ? new Date(v).toLocaleDateString('sk-SK') : '—'

function greetingKey() {
  const hour = new Date().getHours()
  if (hour < 12) return 'morning'
  if (hour < 18) return 'afternoon'
  return 'evening'
}

function MoneyStat({
  icon: Icon,
  tone,
  label,
  value,
  sub
}: {
  icon: LucideIcon
  tone: keyof typeof MONEY_TONE
  label: string
  value: ReactNode
  sub?: ReactNode
}) {
  return (
    <Card className="p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm text-muted-foreground">{label}</div>
          <div className="mt-1 font-display text-3xl font-bold leading-none text-ink">
            {value}
          </div>
          {sub && (
            <div className="mt-2 text-sm text-muted-foreground">{sub}</div>
          )}
        </div>
        <span
          className={cn(
            'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl',
            MONEY_TONE[tone]
          )}
        >
          <Icon className="h-5 w-5" />
        </span>
      </div>
    </Card>
  )
}

function PanelHeader({
  icon: Icon,
  title,
  to,
  viewAll
}: {
  icon: LucideIcon
  title: string
  to: string
  viewAll: string
}) {
  return (
    <div className="mb-4 flex items-center justify-between gap-3">
      <h2 className="m-0 flex items-center gap-2.5 font-display text-lg font-bold tracking-tight text-ink">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Icon className="h-5 w-5" />
        </span>
        {title}
      </h2>
      <Link
        to={to}
        className="inline-flex items-center gap-1 text-sm font-medium text-primary transition-colors hover:underline"
      >
        {viewAll}
        <ArrowRight className="h-3.5 w-3.5" />
      </Link>
    </div>
  )
}

export function LandlordDashboard() {
  const { t } = useTranslation('dashboard')
  const { t: tc } = useTranslation('contract')
  const { t: tf } = useTranslation('flat')
  const { data: profile } = useProfile(true)
  const { data: summary } = useLandlordDashboard()
  const { data: rentsData } = useRents()
  const { data: flatsData } = useFlats()

  const name = profile?.first_name || profile?.email?.split('@')[0] || ''
  const todayISO = new Date().toISOString().slice(0, 10)

  // Upcoming/overdue: unpaid rents, soonest due first.
  const upcoming = ((rentsData?.results as RentRow[]) ?? [])
    .filter((r) => !r.paid)
    .sort((a, b) => (a.due_date ?? '').localeCompare(b.due_date ?? ''))
    .slice(0, 6)

  const flats = (flatsData?.results ?? []).slice(0, 6)

  return (
    <div className="app-chrome space-y-8">
      <header className="flex items-center gap-3">
        <span
          aria-hidden
          className="h-9 w-1.5 shrink-0 rounded-full bg-gradient-to-b from-primary to-peach"
        />
        <div>
          <h1 className="m-0 font-display text-2xl font-bold tracking-tight text-ink lg:text-3xl">
            {t(`landlord.greeting.${greetingKey()}`, { name })}
          </h1>
          <p className="mt-0.5 text-muted-foreground">
            {t('landlord.greeting.subtitle')}
          </p>
        </div>
      </header>

      {/* Money summary band */}
      <div className="grid gap-4 sm:grid-cols-3">
        <MoneyStat
          icon={Clock}
          tone="amber"
          label={t('landlord.owed')}
          value={eur(summary?.unpaid_rents_amount)}
          sub={`${summary?.unpaid_rents_count ?? 0} ${t('landlord.payments')}`}
        />
        <MoneyStat
          icon={CircleCheck}
          tone="green"
          label={t('landlord.received')}
          value={eur(summary?.paid_rents_amount)}
          sub={`${summary?.paid_rents_count ?? 0} ${t('landlord.payments')}`}
        />
        <MoneyStat
          icon={House}
          tone="teal"
          label={t('landlord.occupancy')}
          value={summary?.flats_total ?? 0}
          sub={t('landlord.rentedAvailable', {
            rented: summary?.rented_flats_count ?? 0,
            available: summary?.available_flats_count ?? 0
          })}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Upcoming / overdue rents */}
        <Card className="p-5 sm:p-6">
          <PanelHeader
            icon={Wallet}
            title={t('landlord.upcomingPayments')}
            to="/landlord/payments"
            viewAll={t('landlord.viewAll')}
          />
          {upcoming.length === 0 ? (
            <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-ink/15 bg-ink/[0.015] px-6 py-10 text-center">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary">
                <CircleCheck className="h-5 w-5" />
              </span>
              <span className="text-sm font-medium text-ink">
                {t('landlord.allSettled')}
              </span>
            </div>
          ) : (
            <div className="divide-y divide-ink/[0.06]">
              {upcoming.map((rent) => {
                const overdue = !!rent.due_date && rent.due_date < todayISO
                return (
                  <div
                    key={rent.id}
                    className="flex items-center justify-between gap-3 py-3"
                  >
                    <div className="min-w-0">
                      {rent.flat ? (
                        <Link
                          to={`/landlord/flats/${rent.flat}`}
                          className="truncate font-medium text-ink transition-colors hover:text-primary"
                        >
                          {rent.flat_title || '—'}
                        </Link>
                      ) : (
                        <span className="font-medium text-ink">
                          {rent.flat_title || '—'}
                        </span>
                      )}
                      <div className="text-xs text-muted-foreground">
                        {tc(`rent_table.rents.${rent.payment_type}`, {
                          defaultValue: rent.payment_type
                        })}
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="whitespace-nowrap font-semibold text-ink">
                        {eur(rent.amount)}
                      </div>
                      <div className="mt-0.5 flex items-center justify-end gap-1 text-xs">
                        {overdue ? (
                          <span className="inline-flex items-center gap-1 font-medium text-warning">
                            <AlertTriangle className="h-3.5 w-3.5" />
                            {t('landlord.overdue')} · {formatDate(rent.due_date)}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">
                            {formatDate(rent.due_date)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </Card>

        {/* Flats overview */}
        <Card className="p-5 sm:p-6">
          <PanelHeader
            icon={House}
            title={t('landlord.yourFlats')}
            to="/landlord/flats"
            viewAll={t('landlord.viewAll')}
          />
          {flats.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-ink/15 bg-ink/[0.015] px-6 py-10 text-center text-sm text-muted-foreground">
              {t('landlord.noFlats')}
            </div>
          ) : (
            <div className="divide-y divide-ink/[0.06]">
              {flats.map((flat: FlatList) => {
                const location = flat.location as PlaceOutput | null
                return (
                  <Link
                    key={flat.id}
                    to={`/landlord/flats/${flat.id}`}
                    className="-mx-2 flex items-center justify-between gap-3 rounded-xl px-2 py-3 transition-colors hover:bg-ink/[0.02]"
                  >
                    <div className="min-w-0">
                      <div className="truncate font-medium text-ink">
                        {flat.title || '—'}
                      </div>
                      {location?.village && (
                        <div className="truncate text-xs text-muted-foreground">
                          {location.village}
                        </div>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                      {flat.monthly_rent_sum != null && (
                        <span className="whitespace-nowrap text-sm font-semibold text-ink">
                          {flat.monthly_rent_sum} €
                        </span>
                      )}
                      <span
                        className={cn(
                          'inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold',
                          FLAT_STATUS_TONE[flat.status ?? ''] ??
                            'bg-ink/[0.07] text-ink/60'
                        )}
                      >
                        {tf(`statuses.${flat.status}`)}
                      </span>
                    </div>
                  </Link>
                )
              })}
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}
